import * as Sentry from '@sentry/nextjs';
import axios from 'axios';

import { NOT_FOUND_CODE, UNAUTHENTICATED_CODE } from '~/common/constants/errorCodes';
import { getAuthorizationHeaderFromCookie } from '~/utils/auth';
import { sanitizeSentryExtras } from '~/utils/sentryData';

import { safeJson } from './utils';

import type { AxiosError, AxiosInstance } from 'axios';

export class RestApi {
  protected static handleError<T>(error: AxiosError, expectedStatusCodes?: number[]): T | null {
    const status = error.response?.status;
    const EXPECTED_STATUS = new Set([
      UNAUTHENTICATED_CODE,
      NOT_FOUND_CODE,
      ...(expectedStatusCodes ?? []),
    ]);

    if (EXPECTED_STATUS.has(status ?? 0) || RestApi.isRequestAbort(error)) {
      console.warn('Ignored expected error:', error.message);
      return null;
    }

    if (RestApi.isNetworkError(error)) {
      console.warn('Ignored network/CORS error:', error.message);
      return null;
    }

    Sentry.withScope((scope) => {
      scope.setTag('http.status_code', String(status ?? 'unknown'));
      scope.setTag('http.method', (error.config?.method ?? 'unknown').toUpperCase());
      scope.setTag('http.url', error.config?.url ?? 'unknown');

      const responseData = error.response?.data;

      const responseString = ((): string => {
        try {
          return JSON.stringify(responseData, null, 2).slice(0, 10_000);
        } catch {
          return '[Unserializable response]';
        }
      })();

      scope.setExtras(
        sanitizeSentryExtras({
          response: safeJson(responseData),
          response_string: responseString,
          request: {
            url: error.config?.url,
            method: error.config?.method,
            params: safeJson(error.config?.params),
          },
          axios: {
            code: error.code,
            message: error.message,
            name: error.name,
            status: error.status,
          },
        })
      );

      Sentry.captureException(error);
    });

    return null;
  }

  private static initialize(baseUrl: string | undefined): AxiosInstance {
    return axios.create({
      baseURL: baseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        ...getAuthorizationHeaderFromCookie(),
      },
    });
  }

  private static isNetworkError(error: unknown): boolean {
    // AxiosError type guard
    const axiosError = error as AxiosError | undefined;

    if (!axiosError) {
      return false;
    }

    // 1) No HTTP response at all — usually network/CORS
    if (!axiosError.response) {
      const msg = typeof axiosError.message === 'string' ? axiosError.message : '';
      // common axios messages/codes for network issues
      const networkCodes = new Set(['ERR_NETWORK', 'ENOTFOUND', 'EAI_AGAIN', 'ECONNRESET']);

      if (msg.includes('Network Error') || networkCodes.has(axiosError.code ?? '')) {
        return true;
      }

      // Treat the absence of a response as a network/CORS failure
      return true;
    }

    return false;
  }

  private static isRequestAbort(error: unknown): boolean {
    const axiosError = error as AxiosError & { request?: { aborted?: boolean } };

    return (
      axios.isCancel?.(axiosError) ||
      axiosError.code === 'ECONNABORTED' || // timeout
      axiosError.code === 'ERR_CANCELED' || // axios v1 canceled
      axiosError.code === 'ECONNRESET' || // connection reset by peer
      axiosError.request?.aborted === true || // http.ClientRequest aborted
      (typeof axiosError.message === 'string' && /aborted/i.test(axiosError.message))
    );
  }

  static setAuthorizationHeader(auth: Record<string, string>): void {
    // This mutates shared Axios defaults. It is suitable for browser-side auth,
    // but SSR requests must pass their header per request to avoid one user's
    // token being reused by another concurrent request.
    this.snifferApi.defaults.headers.common.Authorization = auth.authorization;
    this.woofApi.defaults.headers.common.Authorization = auth.authorization;
  }

  protected static snifferApi: AxiosInstance = RestApi.initialize(
    process.env.NEXT_PUBLIC_SNIFFER_API_BASE_URL
  );

  protected static woofApi: AxiosInstance = RestApi.initialize(
    process.env.NEXT_PUBLIC_API_BASE_URL
  );
}
